/**
 * Pull-to-refresh composable for PWA functionality
 * Allows users to refresh the page by pulling down when at top of page
 */

export const usePullToRefresh = (onRefresh: () => Promise<void>) => {
  const isRefreshing = ref(false)
  const pullDistance = ref(0)
  const isPulling = ref(false)
  
  let startY = 0
  let currentY = 0
  const PULL_THRESHOLD = 80 // Minimum pull distance to trigger refresh
  const MAX_PULL_DISTANCE = 120 // Maximum visual pull distance
  
  const handleTouchStart = (e: TouchEvent) => {
    // Only start pull-to-refresh if we're at the top of the page
    if (window.scrollY === 0 && !isRefreshing.value) {
      startY = e.touches[0].clientY
      isPulling.value = false
    }
  }
  
  const handleTouchMove = (e: TouchEvent) => {
    if (window.scrollY === 0 && !isRefreshing.value && startY > 0) {
      currentY = e.touches[0].clientY
      const distance = currentY - startY
      
      if (distance > 0) {
        // Prevent default scroll behavior when pulling down
        e.preventDefault()
        isPulling.value = true
        
        // Calculate pull distance with diminishing returns
        pullDistance.value = Math.min(distance * 0.5, MAX_PULL_DISTANCE)
      }
    }
  }
  
  const handleTouchEnd = async () => {
    if (isPulling.value && pullDistance.value > PULL_THRESHOLD && !isRefreshing.value) {
      isRefreshing.value = true
      
      try {
        await onRefresh()
      } catch (error) {
        console.error('Pull-to-refresh failed:', error)
      } finally {
        isRefreshing.value = false
      }
    }
    
    // Reset values
    startY = 0
    currentY = 0
    isPulling.value = false
    pullDistance.value = 0
  }
  
  const mount = () => {
    document.addEventListener('touchstart', handleTouchStart, { passive: false })
    document.addEventListener('touchmove', handleTouchMove, { passive: false })
    document.addEventListener('touchend', handleTouchEnd, { passive: true })
  }
  
  const unmount = () => {
    document.removeEventListener('touchstart', handleTouchStart)
    document.removeEventListener('touchmove', handleTouchMove)
    document.removeEventListener('touchend', handleTouchEnd)
  }
  
  return {
    isRefreshing: readonly(isRefreshing),
    pullDistance: readonly(pullDistance),
    isPulling: readonly(isPulling),
    mount,
    unmount
  }
}